<template>
  <v-container id="map" class="py-15">
    <h2 class="heading text-center" v-html="t('map.heading')"></h2>

    <!-- 画像の表示 -->
    <v-row class="justify-center mt-4">
      <v-col cols="12" sm="10" md="8" lg="6">
        <img
          @click="openModal"
          width="100%"
          :src="mapImagePath"
          class="map-image my-2 mx-auto"
        />
      </v-col>
    </v-row>

    <!-- 説明文 -->
    <v-row class="justify-center mt-4">
      <v-col cols="12" sm="10" md="8" lg="6">
        <p class="text-caption" v-html="t('map.description')"></p>
      </v-col>
    </v-row>

    <!-- モーダル表示 -->
    <v-dialog
      v-model="isModalOpen"
      :persistent="false"
      :max-width="isMobile ? '100%' : '90%'"
      :fullscreen="isMobile"
      class="modal-card"
      @click="handleDialogClick"
    >
      <div class="zoom-container">
        <div class="image-wrapper">
          <!-- 拡大画像 -->
          <img
            ref="modalImage"
            :src="mapImagePath"
            class="modal-image"
            :style="{ maxWidth: isMobile ? '100%' : '1800px' }"
            draggable="false"
            @mousedown="startDrag"
            @mousemove="onDrag"
            @mouseup="endDrag"
            @mouseleave="endDrag"
          />
        </div>
        <!-- クローズボタン -->
        <v-btn icon class="close-btn" @click.stop="closeModal">×</v-btn>
      </div>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import localeData from "@/locales/AreaMap.js";
import { getBasePath } from '@/utils/basePath';

const { t, locale } = useI18n();
const isModalOpen = ref(false);
const isMobile = window.innerWidth <= 767; // スマホ判定
const modalImage = ref(null); // 画像を参照するための ref

// ベースパスを取得
const basePath = computed(() => getBasePath());

// 画像パスを構築
const mapImagePath = computed(() => `${basePath.value}${t('map.image')}`);

// ドラッグ用のステート
const isDragging = ref(false);
const dragStartX = ref(0);
const dragStartY = ref(0);
const imgX = ref(0); // 画像の横方向の初期位置
const imgY = ref(0); // 画像の縦方向の初期位置

// モーダルを開く関数
const openModal = () => {
  isModalOpen.value = true;
  resetImagePosition(); // 画像を初期位置にリセット
};

// モーダルを閉じる関数
const closeModal = () => {
  isModalOpen.value = false;
  resetImagePosition();
};

// 画像をドラッグ開始時の処理
const startDrag = (event) => {
  event.preventDefault(); // デフォルトの動作を無効化（特に画像のドラッグを防ぐため）
  isDragging.value = true;
  dragStartX.value = event.clientX - imgX.value;
  dragStartY.value = event.clientY - imgY.value;
};

// 画像のドラッグ中の処理
const onDrag = (event) => {
  if (!isDragging.value) return;
  imgX.value = event.clientX - dragStartX.value;
  imgY.value = event.clientY - dragStartY.value;
  const modalImageElement = modalImage.value;
  modalImageElement.style.transform = `translate(${imgX.value}px, ${imgY.value}px)`;
};

// ドラッグ終了時の処理
const endDrag = () => {
  isDragging.value = false;
};

// 画像の位置をリセット
const resetImagePosition = () => {
  imgX.value = 0;
  imgY.value = 0;
  if (modalImage.value) {
    modalImage.value.style.transform = `translate(0px, 0px)`;
  }
};

// モーダル外側をクリックした際に閉じる処理
const handleDialogClick = (event) => {
  // スマホのみ画像外をタップした場合に閉じる
  if (isMobile && event.target !== modalImage.value) {
    closeModal();
  }
};
</script>

<style scoped lang="scss">
.modal-card {
  background-color: rgba(0, 0, 0, 0.8);
}

/* 画像を中央配置するためのコンテナ */
.zoom-container {
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  transform-origin: center center;
  position: relative;
  width: 100%;
  height: 100vh; /* モーダルの高さを画面全体に設定 */
}

/* 画像を中央に配置するラッパー */
.image-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  position: relative; /* 画像が動く範囲を制限するために relative に設定 */
}

.modal-image {
  height: auto;
  width: 100%; /* スマホ時は常に100%表示 */
  object-fit: contain;
  margin: auto;
  cursor: grab; /* 画像がドラッグ可能であることを示す */
  position: relative;
}

.modal-image:active {
  cursor: grabbing;
}

/* クローズボタン */
.close-btn {
  position: absolute;
  right: 12px;
  top: 12px;
  font-size: 24px;
  color: white;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  padding: 4px;
  z-index: 10; /* クローズボタンが常に最前面に表示されるように設定 */
}

/* スマホ用のスタイル */
@media screen and (max-width: 767px) {
  .zoom-container {
    align-items: center;
    justify-content: center;
  }

  .modal-image {
    width: 100%; /* スマホでは常に100%表示 */
    height: auto;
    cursor: auto; /* スマホではカーソルを通常の矢印にする */
  }
}
</style>
